<template>
  <div class="d-flex flex-column">
    <div class="row justify-content-between mb-4">
      <div class="col-12 col-sm-4 d-flex">
        <card :title="$t('sections.cdrmLandingPage.numbers.title')" divider>
          <div class="row">
            <div class="d-flex flex-column col-auto">
              <HorizontalDetailTitle
                :label="$t('sections.cdrmLandingPage.numbers.cdrGenerated')"
              />
              <HorizontalDetailTitle
                :label="$t('sections.cdrmLandingPage.numbers.cdrProcessed')"
              />
              <HorizontalDetailTitle
                :label="$t('sections.cdrmLandingPage.numbers.cdrError')"
              />
              <HorizontalDetailTitle
                :label="
                  $t('sections.cdrmLandingPage.numbers.sessionsWithoutCDR')
                "
              />
              <HorizontalDetailTitle
                :label="$t('sections.cdrmLandingPage.numbers.cdrResent')"
              />
            </div>
            <div class="d-flex flex-column col-auto flex-fill pl-4">
              <HorizontalDetailInput :field="cdrsGenerated" :edit="false" />
              <HorizontalDetailInput :field="cdrsProcessed" :edit="false" />
              <HorizontalDetailInput :field="cdrsError" :edit="false" />
              <HorizontalDetailInput
                :field="sessionsWithoutCDRLastWeek"
                :edit="false"
              />
              <HorizontalDetailInput :field="cdrsResent" :edit="false" />
            </div>
          </div>
        </card>
      </div>
      <div class="col-12 col-sm-4">
        <BarChart
          v-if="!getLoading"
          :labels="cdrsByPartyIdLabels"
          :datasets="cdrsByPartyIdDatasets"
          :title="$t('sections.cdrmLandingPage.graphs.cdrByPartyId')"
          class="mb-3"
        />
      </div>
      <div class="col-12 col-sm-4">
        <PieChart
          v-if="!getLoading"
          :labels="cdrsByStateLabels"
          :datasets="cdrsByStateDatasets"
          :title="$t('sections.cdrmLandingPage.graphs.cdrByStatus')"
          class="mb-3"
        />
      </div>
    </div>
    <card :title="$t('sections.cdrmLandingPage.errorsTable.title')">
      <PaginationWrapper
        export
        :page="errorsPage"
        :totalCount="errorsTotalCount"
        @onPageChange="handleErrorsPageChange"
        @exportTable="exportErrorsList"
      >
        <div slot="table">
          <base-table :data="getErrorCDRs">
            <el-table-column
              prop="id"
              :label="$t('sections.cdrmLandingPage.errorsTable.cdrId')"
            />
            <el-table-column
              prop="errorId"
              :label="$t('sections.cdrmLandingPage.errorsTable.errorId')"
            />
            <el-table-column
              prop="errorMessage"
              :label="$t('sections.cdrmLandingPage.errorsTable.errorMessage')"
            />
            <el-table-column fixed="right" width="60">
              <template slot="header">
                <div class="button-placeholder" />
              </template>
              <template slot-scope="props">
                <div class="d-flex justify-content-center">
                  <el-dropdown trigger="click">
                    <div class="outline-icon-button">
                      <font-awesome-icon
                        icon="fa-regular fa-ellipsis-vertical"
                      />
                    </div>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item>
                        <div @click="resendCDR(props.row)">
                          {{
                            $t(
                              "sections.cdrmLandingPage.errorsTable.options"
                            )[0]
                          }}
                        </div>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </template>
            </el-table-column>
          </base-table>
        </div>
      </PaginationWrapper>
    </card>
    <card :title="$t('sections.cdrmLandingPage.sessionsWithoutCDRTable.title')">
      <PaginationWrapper
        export
        :page="sessionsWithoutCDRsPage"
        :totalCount="sessionsWithoutCDR"
        @onPageChange="handleSessionsWithoutCDRsPageChange"
        @exportTable="exportSessionsWithoutCDRsList"
      >
        <CDRLandingPageSearchOptions
          @onSearch="handleSearch"
          slot="search-options"
        />
        <div slot="table">
          <base-table :data="getSessions">
            <el-table-column
              prop="id"
              :label="
                $t(
                  'sections.cdrmLandingPage.sessionsWithoutCDRTable.sessionsId'
                )
              "
            />
            <el-table-column
              prop="pdgrCdrInfo.status"
              :label="
                $t('sections.cdrmLandingPage.sessionsWithoutCDRTable.status')
              "
            />
            <el-table-column
              :label="
                $t(
                  'sections.cdrmLandingPage.sessionsWithoutCDRTable.lastUpdated'
                )
              "
            >
              <template slot-scope="props">
                {{
                  props.row.last_updated
                    ? formatTime(props.row.last_updated)
                    : props.row.last_updated
                }}
              </template>
            </el-table-column>
            <el-table-column
              prop="pdgrCdrInfo.errorMessage"
              :label="
                $t(
                  'sections.cdrmLandingPage.sessionsWithoutCDRTable.errorMessage'
                )
              "
            />
            <el-table-column fixed="right" width="60">
              <template slot="header">
                <div class="button-placeholder" />
              </template>
              <template slot-scope="props">
                <div
                  class="d-flex justify-content-center"
                  v-if="getUserProfile === 'MOBIE' && cdrsPermissions.operate"
                >
                  <el-dropdown trigger="click">
                    <div class="outline-icon-button">
                      <font-awesome-icon
                        icon="fa-regular fa-ellipsis-vertical"
                      />
                    </div>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item>
                        <div @click="generateCDR(props.row.id)">
                          {{
                            $t(
                              "sections.cdrmLandingPage.sessionsWithoutCDRTable.options"
                            )[0]
                          }}
                        </div>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </template>
            </el-table-column>
          </base-table>
        </div>
      </PaginationWrapper>
    </card>
  </div>
</template>
<script>
// eslint-disable no-underscore-dangle
import { mapGetters } from 'vuex';
import { formatTime } from '@/utils';
import HorizontalDetailTitle from '@/components/ui/HorizontalDetailTitle.vue';
import HorizontalDetailInput from '@/components/ui/HorizontalDetailInput.vue';
import BarChart from '@/components/ui/charts/BarChart.vue';
import PieChart from '@/components/ui/charts/PieChart.vue';
import PaginationWrapper from '@/components/sections/common/PaginationWrapper.vue';
import CDRLandingPageSearchOptions from '@/components/sections/cdrs/CDRLandingPageSearchOptions';

export default {
  name: 'CDRsManagerLandingPage',
  components: {
    HorizontalDetailTitle,
    HorizontalDetailInput,
    BarChart,
    PieChart,
    PaginationWrapper,
    CDRLandingPageSearchOptions,
  },
  data() {
    return {
      cdrsGenerated: 0,
      cdrsProcessed: 0,
      cdrsError: 0,
      cdrsResent: 0,
      sessionsWithoutCDR: 0,
      sessionsWithoutCDRLastWeek: 0,
      cdrsByPartyIdLabels: [],
      cdrsByPartyIdValues: [],
      cdrsByPartyIdDatasets: [],
      cdrsByStateLabels: [],
      cdrsByStateValues: [],
      cdrsByStateDatasets: [],
      errorsPage: 1,
      errorsTotalCount: 0,
      sessionsWithoutCDRsPage: 1,
      query: {},
    };
  },
  computed: {
    ...mapGetters([
      'getLoading',
      'getErrorCDRs',
      'getSessions',
      'getUserProfile',
      'getPagePermissions',
    ]),
    cdrsPermissions() {
      return this.getPagePermissions('CDRs Manager - CDRs');
    },
  },
  mounted() {
    const dateTo = new Date();
    dateTo.setHours(23, 59, 59, 999);
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - 6);
    dateFrom.setHours(0, 0, 0, 0);
    this.query = { dateFrom, dateTo };
    this.fetchData();
  },
  methods: {
    formatTime,
    async fetchData() {
      this.$store.dispatch('storeLoading', true);
      try {
        const errorCDRsPromise = this.$store.dispatch('storeErrorCDRs', {
          query: {},
          page: this.errorsPage,
        });
        const sessionsWithoutCDRPromise = this.$store.dispatch('storeSessions', {
          query: {
            withoutCDR: true,
            ...this.query,
          },
          page: this.sessionsWithoutCDRsPage,
        });
        const sessionsAnalyticsPromise = this.$store.dispatch(
          'getSessionsAnalytics',
        );
        const analyticsPromise = this.$store.dispatch('getCDRsAnalytics');

        const promises = await Promise.allSettled([
          errorCDRsPromise,
          sessionsWithoutCDRPromise,
          sessionsAnalyticsPromise,
          analyticsPromise,
        ]);

        this.errorsTotalCount = promises[0].status === 'fulfilled' ? promises[0].value : 0;
        const sessionsWithoutCDRResult = promises[1].status === 'fulfilled' ? promises[1].value : 0;
        const sessionsAnalytics = promises[2].status === 'fulfilled' ? promises[2].value : 0;
        const analytics = promises[3].status === 'fulfilled' ? promises[3].value : undefined;

        this.sessionsWithoutCDR = sessionsWithoutCDRResult.totalCount;
        this.sessionsWithoutCDRLastWeek = sessionsAnalytics.totalCountWithoutCDRLastWeek;
        this.cdrsGenerated = analytics.cdrsGenerated;
        this.cdrsProcessed = analytics.cdrsProcessed;
        this.cdrsError = analytics.cdrsError;
        this.cdrsResent = analytics.cdrsResent;

        this.cdrsByPartyIdLabels = analytics.countByPartyId.labels;
        this.cdrsByPartyIdValues = analytics.countByPartyId.values;
        this.cdrsByPartyIdDatasets = [
          {
            label: this.$t('sections.cdrmLandingPage.graphs.cdrByPartyId'),
            borderColor: '#1a84cf',
            backgroundColor: 'rgba(26, 132, 207, 0.5)',
            data: this.cdrsByPartyIdValues,
          },
        ];

        this.cdrsByStateLabels = analytics.cdrsByStatus.labels;
        this.cdrsByStateValues = analytics.cdrsByStatus.values;
        this.cdrsByStateDatasets = [
          {
            backgroundColor: ['#1a84cf', '#56a3e0', '#0063aa'],
            borderColor: ['#1a84cf', '#56a3e0', '#0063aa'],
            data: this.cdrsByStateValues,
          },
        ];
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.dispatch('storeLoading', false);
      }
    },
    async fetchCDRsErrorsData() {
      this.$store.dispatch('storeLoading', true);
      try {
        this.errorsTotalCount = await this.$store.dispatch('storeErrorCDRs', {
          query: {},
          page: this.errorsPage,
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.dispatch('storeLoading', false);
      }
    },
    async fetchSessionsData() {
      this.$store.dispatch('storeLoading', true);
      try {
        const { totalCount } = await this.$store.dispatch(
          'storeSessions',
          {
            query: {
              withoutCDR: true,
              ...this.query,
            },
            page: this.sessionsWithoutCDRsPage,
          },
        );
        this.sessionsWithoutCDR = totalCount;
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.dispatch('storeLoading', false);
      }
    },
    async resendCDR(cdr) {
      try {
        await this.$store.dispatch('resendCDR', cdr);
        this.$message.success(this.$t('components.alerts.resendSuccess'));
      } catch (e) {
        this.$message.error(this.$t('components.alerts.error'));
      }
    },
    async generateCDR(id) {
      try {
        await this.$store.dispatch('generateCDR', id);
        this.$message.success(this.$t('components.alerts.generateSuccess'));
        await this.fetchSessionsData();
      } catch (e) {
        this.$message.error(this.$t('components.alerts.error'));
      }
    },
    async handleErrorsPageChange(page) {
      this.errorsPage = page;
      await this.fetchCDRsErrorsData();
    },
    async handleSessionsWithoutCDRsPageChange(page) {
      this.sessionsWithoutCDRsPage = page;
      await this.fetchSessionsData();
    },
    async exportErrorsList() {
      try {
        const file = await this.$store.dispatch('exportList', {
          query: { error: true },
          type: 'cdrs',
        });

        if (file.data === '') {
          this.$message.error(
            this.$t('components.alerts.exportFileError', { type: 'cdrs' }),
          );
        } else {
          const fileURL = window.URL.createObjectURL(new Blob([file.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute(
            'download',
            `CDRs-errors-${new Date()
              .toISOString()
              .slice(0, 10)
              .replaceAll('-', '')}.csv`,
          );
          document.body.appendChild(fileLink);

          fileLink.click();
          this.$message.success(
            this.$t('components.alerts.exportSuccess', { type: 'CDRs errors' }),
          );
        }
      } catch (e) {
        this.$message.error(
          this.$t('components.alerts.exportError', { type: 'CDRs errors' }),
        );
        console.log(e);
      }
    },
    async exportSessionsWithoutCDRsList() {
      try {
        const file = await this.$store.dispatch('exportList', {
          query: { ...this.query, withoutCDR: true },
          type: 'sessionsWithoutCDR',
        });

        if (file.data === '') {
          this.$message.error(
            this.$t('components.alerts.exportFileError', { type: 'sessions' }),
          );
        } else {
          const fileURL = window.URL.createObjectURL(new Blob([file.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute(
            'download',
            `sessions-without-CDR-${new Date()
              .toISOString()
              .slice(0, 10)
              .replaceAll('-', '')}.csv`,
          );
          document.body.appendChild(fileLink);

          fileLink.click();
          this.$message.success(
            this.$t('components.alerts.exportSuccess', {
              type: 'sessions without CDR',
            }),
          );
        }
      } catch (e) {
        this.$message.error(
          this.$t('components.alerts.exportError', {
            type: 'sessions without CDR',
          }),
        );
        console.log(e);
      }
    },
    async handleSearch(query) {
      this.sessionsWithoutCDRsPage = 1;
      this.query = query;
      await this.fetchSessionsData();
    },
  },
};
</script>
